import { useEffect, useMemo, useRef, useState } from 'react';

import { Link, Menu, useSlotRecipe } from '@chakra-ui/react';

import { IconsLinear } from '@nocowanie/icons';

import { ExpandableMenuItem } from '@app/components/molecules/expandable-menu-item';
import { modifyLink } from '@app/helpers/navigation.helpers';
import { NavGroupModel, NavLinkModel } from '@app/models';

import navLinks from './navigation-links.json';

export const Navigation = ({ triggerText }: { triggerText: string }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [openMenuIndex, setOpenMenuIndex] = useState<string | null>(null);

    const recipe = useSlotRecipe({ key: 'navigation' });
    const styles = recipe();

    const handleToggleMenu = (index: string) => {
        setOpenMenuIndex(prevIndex => (prevIndex === index ? null : index));
    };

    const menuTriggerRef = useRef(null);

    const handleCloseNav = () => {
        setOpenMenuIndex(null);
        setIsMenuOpen(false);
    };

    useEffect(() => {
        if (!isMenuOpen) {
            return;
        }

        window.addEventListener('scroll', handleCloseNav);

        return () => {
            window.removeEventListener('scroll', handleCloseNav);
        };
    }, [isMenuOpen]);

    const filteredNavLinks = useMemo(
        () => navLinks.filter((navGroup: NavGroupModel) => !navGroup.mobileOnly),
        [],
    );

    const renderNavLinks = useMemo(
        () =>
            filteredNavLinks.map((navGroup: NavGroupModel) => (
                <Menu.ItemGroup key={navGroup.navGroupId} title={navGroup.navGroupTitle}>
                    <Menu.Item value={navGroup.navGroupId} py={3} px={5} css={styles.navGroupTitle}>
                        {navGroup.navGroupTitle}
                    </Menu.Item>
                    {navGroup.navLinks.map((navLink: NavLinkModel) =>
                        navLink.isExpandable ? (
                            <ExpandableMenuItem
                                key={navLink.navLinkId}
                                navLink={navLink}
                                isOpen={openMenuIndex === navLink.navLinkId}
                                toggleMenu={() => handleToggleMenu(navLink.navLinkId)}
                            />
                        ) : (
                            <Menu.Item
                                value={navLink.navLinkId}
                                key={navLink.navLinkId}
                                asChild
                                py={2}
                                px={4}
                                color={'secondary.solid'}
                            >
                                <Link variant={'menuLinkBase'} href={modifyLink(navLink)}>
                                    {navLink.navLinkLabel}
                                </Link>
                            </Menu.Item>
                        ),
                    )}
                </Menu.ItemGroup>
            )),
        [filteredNavLinks, openMenuIndex],
    );

    return (
        <Menu.Root
            open={isMenuOpen}
            onOpenChange={details =>
                setTimeout(() => (details.open ? setIsMenuOpen(true) : handleCloseNav()), 0)
            }
            lazyMount={false}
        >
            <Menu.Trigger ref={menuTriggerRef} css={styles.button}>
                {triggerText}
                <IconsLinear.ArrowDown2 fontSize={'sm'} ml={2} />
            </Menu.Trigger>
            <Menu.Positioner>
                {/* minH - necessary to ensure correct visibility of all links in the expanding categories */}
                <Menu.Content position={'relative'} zIndex={5} p={0} minH={'475px'}>
                    {renderNavLinks}
                </Menu.Content>
            </Menu.Positioner>
        </Menu.Root>
    );
};
